<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'; // 关键：导入 nextTick
import { Logo, Footer, Style3dViewer, Watermark } from '@repo/components';
import { ShareList, Toolbar, FabricList } from '@/components';
import { useShareStore } from '@/store/share.ts';
import { storeToRefs } from '@repo/stores';
import { useStyle3d } from '@repo/style3d';

const { centerizeOnResize } = useStyle3d();

const shareStore = useShareStore();
const { carouselList, logoUrl, fabricList, styleThumb, styleName } = storeToRefs(shareStore);

// 获取DOM引用 + 动态高度变量
const listsContainerRef = ref<HTMLDivElement | null>(null);
const style3dRef = ref<HTMLDivElement | null>(null);
const style3dHeight = ref<string>('100%');
const footerHeight = 30; // 与样式中footer高度一致

/**
 * 检查图片URL是否有效，无效则使用备用URL
 * @param primaryUrl 首选图片URL
 * @param fallbackUrl 备用图片URL
 * @returns Promise<string> 最终可用的图片URL
 */
async function getValidImageUrl(
  primaryUrl: string,
  fallbackUrl: string
): Promise<string> {
  return new Promise((resolve) => {
    if (!primaryUrl) {
      resolve(fallbackUrl);
      return;
    }

    const img = new Image();
    const timeoutId = setTimeout(() => {
      img.onerror = null;
      resolve(fallbackUrl);
    }, 5000);

    img.onload = () => {
      clearTimeout(timeoutId);
      resolve(primaryUrl);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      resolve(fallbackUrl);
    };

    img.src = primaryUrl;
  });
}

// 动态计算style3d高度的核心函数
function calculateStyle3dHeight() {
  if (!listsContainerRef.value || !style3dRef.value) return;

  // 获取列表容器实际渲染高度
  const listsHeight = listsContainerRef.value.offsetHeight;
  // 视口高度 - footer高度 - 列表高度 = style3d可用高度
  const availableHeight = window.innerHeight - footerHeight - listsHeight;
  // 防止高度为负数
  style3dHeight.value = `${Math.max(availableHeight, 0)}px`;

  // 等待浏览器完成布局重排后再调用 centerizeOnResize
  nextTick(() => {
    // 使用 requestAnimationFrame 确保在浏览器下一帧渲染前调用
    requestAnimationFrame(() => {
      centerizeOnResize();
    });
  });
}

// 监听列表变化，动态调整高度
watch(
  [() => fabricList.value, () => carouselList.value],
  () => {
    // 等待DOM更新完成后再计算高度
    nextTick(() => {
      calculateStyle3dHeight();
    });
  },
  { immediate: true, deep: true }
);

// 原有逻辑：监听图片URL
const finalImageUrl = ref<string>('');
watch(
  () => styleThumb.value,
  async (urls) => {
    const validUrl = await getValidImageUrl(urls.primaryUrl, urls.fallbackUrl);
    finalImageUrl.value = validUrl;
  },
  { immediate: true, deep: false }
);

// 窗口大小变化监听
function handleResize() {
  calculateStyle3dHeight();
}

// 生命周期钩子（初始化 + 销毁监听）
onMounted(() => {
  // 初始化时计算一次高度
  nextTick(() => {
    calculateStyle3dHeight();
  });
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  // 移除监听，防止内存泄漏
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <div class="container">
    <Watermark :text="shareStore.enterpriseName" zIndex="1" rotate="300" />
    <div class="main-content">
      <Logo class="logo" :logo-url="logoUrl" />
      <!-- 绑定动态高度 + 添加ref -->
      <div class="style3d" ref="style3dRef" :style="{ height: style3dHeight }">
        <Style3dViewer />
      </div>
      <Toolbar class="toolbar" />
    </div>
    <!-- 给列表容器添加ref -->
    <div class="lists-container" ref="listsContainerRef">
      <FabricList 
        v-if="fabricList && fabricList.length > 0" 
        :fabrics="fabricList" 
        :firstThumb="finalImageUrl" 
        :firstName="styleName" 
        class="share-list"  
      />
      <ShareList 
        v-if="carouselList && carouselList.length > 0" 
        :carouselList="carouselList" 
        class="share-list" 
      />
    </div>
    <Footer class="footer" />
  </div>
</template>

<style scoped>
.container {
  background-color: #31363B;
  height: 100vh;
  width: 100%;
  display: grid;
  grid-template-rows: 1fr auto auto;
  grid-template-columns: 1fr;
}

.main-content {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  grid-row: 1;
}

.lists-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  /*background-color: #31363B;*/
  background-color: transparent;
  grid-row: 2;
  overflow-x: hidden; /* 防止轮播图横向溢出影响页面宽度和3D模型居中 */
  z-index: 2;
}

.footer {
  height: 30px;
  grid-row: 3;
}

.qrScan {
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
}

.logo {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 10;
}

.toolbar {
  position: absolute;
  right: 5px;
  top: 8px;
}

.share-list {
  width: 100%;
  flex-shrink: 0;
}

.style3d {
  background-color: #00000000; /*31363B*/
  width: 100%;
  /* 移除固定height:100%，改为动态绑定 */
  position: relative;
  z-index: 2;
}
</style>